import React, { useState, useMemo, useEffect } from "react";
import Modal from "./Modal";
import TimeProgress from "./TimeProgress";
import TimeMeasurements from "./TimeMeasurements";
import LifeGrid from "./LifeGrid";
import Header from "./Header";
import Footer from "./Footer";
import Quote from "./Quote.js";
import GoalTracker from "./GoalTracker.js";
import Statistics from "./Statistics.js";
import Milestones from "./Milestones.js";
import BackgroundEmojis from "./BackgroundEmojis.js";

const LandingPage = () => {
  const [name, setName] = useState("Aryan");
  const [birthDate, setBirthDate] = useState(new Date("2001-12-30"));
  const [weeksLeft, setWeeksLeft] = useState(0);
  const [lifeProgress, setLifeProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [timeMeasurements, setTimeMeasurements] = useState({
    hours: 0,
    days: 0,
    weeks: 0,
    months: 0,
    years: 0,
    seconds: 0,
  });

  const calculateWeeksLeft = useMemo(() => {
    try {
      if (!birthDate || isNaN(birthDate.getTime())) {
        return 0;
      }
      const endDate = new Date(birthDate);
      endDate.setFullYear(endDate.getFullYear() + 60);
      const timeDiff = endDate.getTime() - new Date().getTime();
      const weeksDiff = Math.ceil(timeDiff / (1000 * 3600 * 24 * 7));
      return weeksDiff > 0 ? weeksDiff : 0;
    } catch {
      return 0;
    }
  }, [birthDate]);

  const calculateLifeProgress = useMemo(() => {
    const totalWeeks = 60 * 52;
    const weeksPassed = totalWeeks - weeksLeft;
    return (weeksPassed / totalWeeks) * 100;
  }, [weeksLeft]);

  useEffect(() => {
    setWeeksLeft(calculateWeeksLeft);
    setLifeProgress(calculateLifeProgress);
  }, [birthDate, calculateWeeksLeft, calculateLifeProgress]);

  useEffect(() => {
    // Safety check for valid birthDate
    if (!birthDate || isNaN(birthDate.getTime())) {
      return;
    }

    const timer = setInterval(() => {
      try {
        const now = new Date();
        const endDate = new Date(birthDate);
        endDate.setFullYear(endDate.getFullYear() + 60);

        const diff = endDate - now;

        // Only update if diff is valid
        if (!isNaN(diff) && diff >= 0) {
          setTimeMeasurements({
            years: Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24 * 365))),
            months: Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24 * 30)) % 12),
            weeks: Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24 * 7)) % 4),
            days: Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)) % 7),
            hours: Math.max(0, Math.floor(diff / (1000 * 60 * 60)) % 24),
            seconds: Math.max(0, Math.floor(diff / 1000) % 60),
          });
        }
      } catch (err) {
        // Silently handle errors to prevent crashes
        console.error('Error calculating time measurements:', err);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [birthDate]);

  const handleNameChange = (e) => {
    setName(e.target.value);
  };

  const handleBirthDateChange = (newDate) => {
    // Additional validation in parent component as safety check
    if (newDate && !isNaN(newDate.getTime())) {
      const today = new Date();
      const ageInYears = (today - newDate) / (1000 * 60 * 60 * 24 * 365.25);
      
      // Only set if date is valid and reasonable
      if (ageInYears >= 0 && ageInYears <= 120 && newDate <= today) {
        setBirthDate(newDate);
      }
    }
  };

  // Open modal function
  const openModal = () => setShowModal(true);
  // Close modal function
  const closeModal = () => setShowModal(false);

  return (
    <div className="min-h-screen bg-black text-white relative">
      <BackgroundEmojis />
      {showModal && (
        <Modal
          name={name}
          birthDate={birthDate}
          setName={handleNameChange}
          setBirthDate={handleBirthDateChange}
          setShowModal={closeModal} // Correctly passes close function
        />
      )}

      <div className="container mx-auto px-4 py-28 relative z-10">
        <Header name={name} weeksLeft={weeksLeft} setShowModal={openModal} />
        <TimeProgress lifeProgress={lifeProgress} />
        <Statistics birthDate={birthDate} weeksLeft={weeksLeft} lifeProgress={lifeProgress} />
        <Milestones birthDate={birthDate} />
        <TimeMeasurements timeMeasurements={timeMeasurements} />
        <LifeGrid weeksLeft={weeksLeft} birthDate={birthDate} />
        <Quote />
        <GoalTracker />
        <Footer />
      </div>
    </div>
  );
};

export default LandingPage;
